import React from 'react';
import { ClipboardList, Users, ArrowRight } from 'lucide-react';

const CALENDLY_URL = "https://calendly.com/arthur-vacogne-rdv/rdv-echange-1er-prise-de-contact";

/* Custom 4-piece puzzle icon — clean 2x2 grid of interlocking tiles */
const Puzzle4Icon = ({ size = 26, strokeWidth = 1.6, color = "white" }) => (
  <svg
    width={size} height={size}
    viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Top-left tile with right tab + bottom tab */}
    <path d="M3 3 H9 V5 A1.5 1.5 0 0 1 10.5 6.5 A1.5 1.5 0 0 1 9 8 V10 H7 A1.5 1.5 0 0 0 5.5 11.5 A1.5 1.5 0 0 0 7 13 V10.5 H3 Z" />
    {/* Top-right tile with left socket + bottom tab */}
    <path d="M13 3 H21 V10 H17 A1.5 1.5 0 0 1 18.5 11.5 A1.5 1.5 0 0 1 17 13 V10.5 H13 V8 A1.5 1.5 0 0 0 11.5 6.5 A1.5 1.5 0 0 0 13 5 Z" />
    {/* Bottom-left tile */}
    <path d="M3 13.5 H7 A1.5 1.5 0 0 1 8.5 15 A1.5 1.5 0 0 1 7 16.5 V13.5 H9 V21 H3 Z" />
    {/* Bottom-right tile */}
    <path d="M13 13.5 V16.5 A1.5 1.5 0 0 0 11.5 15 A1.5 1.5 0 0 0 13 13.5 H17 V21 H21 V13.5 Z" />
  </svg>
);

const ApproacheSection = () => {
  return (
    <section id="a-propos" className="section">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="section-label">Mon accompagnement</span>
          <h2 className="heading-1 mb-3">Une approche simple et accessible</h2>
          <p className="body-large text-muted" style={{ maxWidth: '760px', margin: '0 auto 2rem' }}>
            Votre accompagnement personnalisé pour donner du sens à votre épargne et à vos projets.
          </p>
        </div>

        {/* New intro paragraph - before "Ma démarche" */}
        <div style={{
          maxWidth: '880px',
          margin: '0 auto 3.5rem',
          padding: '2rem 2.25rem',
          background: 'rgba(172, 153, 105, 0.06)',
          border: '1px solid rgba(172, 153, 105, 0.18)',
          borderRadius: '0.75rem',
          borderLeft: '4px solid var(--primary-gold)'
        }}>
          <p className="body-medium" style={{ marginBottom: 0, lineHeight: 1.85, fontSize: '1rem' }}>
            Vous accompagner pour <strong>organiser, valoriser et optimiser</strong> vos finances
            avec une approche totalement différente des approches traditionnelles et bancaires.
            Chaque situation patrimoniale est unique. En fonction de vos horizons de temps et de
            votre sensibilité au risque, une solution adaptée sera recherchée. Une proposition
            d'investissement ou d'organisation vous sera proposée.
          </p>
        </div>

        {/* Démarche - 3 steps with new content */}
        <div className="text-center" style={{ marginBottom: '2rem' }}>
          <h3 className="heading-2" style={{ marginBottom: '0.5rem' }}>Ma démarche</h3>
          <p className="body-medium text-muted" style={{ maxWidth: '620px', margin: '0 auto 2.5rem' }}>
            Trois étapes essentielles pour vous accompagner efficacement
          </p>
        </div>

        <div className="grid-3" style={{ marginBottom: '2rem' }}>
          {[
            {
              icon: ClipboardList,
              title: 'Clarifier votre situation',
              description: 'Réaliser un état des lieux complet et définir une vision claire pour votre patrimoine.',
              testid: 'approach-step-1',
              highlight: false
            },
            {
              icon: Puzzle4Icon,
              title: 'Structurer vos décisions',
              description: 'Construire des solutions sur mesure et adaptées à vos objectifs.',
              testid: 'approach-step-2',
              highlight: true
            },
            {
              icon: Users,
              title: 'Accompagnement, mise en place & pilotage',
              description: 'Vous accompagner dans la durée et ajuster votre stratégie à chaque étape.',
              testid: 'approach-step-3',
              highlight: false
            }
          ].map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={idx}
                className="card text-center"
                data-testid={step.testid}
                style={{
                  background: step.highlight ? 'rgba(172, 153, 105, 0.06)' : 'var(--bg-card)',
                  borderRadius: '1.25rem',
                  border: '1px solid rgba(58, 46, 15, 0.35)',
                  boxShadow: '0 4px 18px rgba(58, 46, 15, 0.06)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(58, 46, 15, 0.12)';
                  e.currentTarget.style.borderColor = 'var(--primary-brown)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 18px rgba(58, 46, 15, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(58, 46, 15, 0.35)';
                }}
              >
                {/* Icon medallion - same style as Services cards */}
                <div style={{
                  position: 'relative',
                  width: '100px',
                  height: '100px',
                  margin: '0 auto 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Soft circular halo backdrop */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(45deg, rgba(172, 153, 105, 0.12), transparent)',
                    borderRadius: '50%'
                  }} />
                  {/* Rounded-square gold→brown tile (matches Services) */}
                  <div className="icon-tile icon-tile-shimmer" style={{
                    position: 'relative',
                    width: '70px',
                    height: '70px',
                    borderRadius: '1rem',
                    background: 'linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-brown) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 22px rgba(58, 46, 15, 0.18)'
                  }}>
                    <Icon size={28} strokeWidth={1.7} color="white" />
                  </div>
                </div>

                <h4
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: '1.35rem',
                    fontWeight: '600',
                    marginBottom: '0.75rem',
                    color: 'var(--primary-brown)',
                    lineHeight: 1.3,
                    letterSpacing: '-0.005em'
                  }}
                >
                  {step.title}
                </h4>

                {/* Decorative gold separator */}
                <div style={{
                  width: '36px',
                  height: '2px',
                  background: 'var(--primary-gold)',
                  margin: '0 auto 1rem',
                  borderRadius: '2px'
                }} />

                <p className="body-medium text-muted" style={{ marginBottom: 0 }}>
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Strong CTA below the 3 démarche cards - gold gradient for max visibility */}
        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="approach-rdv-cta"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.7rem',
              padding: '1.05rem 2.4rem',
              background: 'linear-gradient(135deg, var(--primary-gold) 0%, #c9b576 50%, var(--primary-gold) 100%)',
              color: 'white',
              fontWeight: '700',
              fontSize: '1rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              borderRadius: '2.5rem',
              textDecoration: 'none',
              boxShadow: '0 12px 32px rgba(172, 153, 105, 0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
              transition: 'transform 0.25s ease, box-shadow 0.25s ease',
              border: '1px solid rgba(255,255,255,0.15)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 18px 42px rgba(172, 153, 105, 0.6), inset 0 1px 0 rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(172, 153, 105, 0.45), inset 0 1px 0 rgba(255,255,255,0.25)';
            }}
          >
            Rendez-vous gratuit
            <ArrowRight size={18} />
          </a>
          <p style={{
            marginTop: '0.85rem',
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            letterSpacing: '0.02em'
          }}>
            Premier échange offert · sans engagement
          </p>
        </div>

      </div>
    </section>
  );
};

export default ApproacheSection;
